import { useState } from 'react';
import { CONDITIONS, VIDEO_SYSTEMS, CATEGORIES, PLATFORMS } from '@siliconvault/shared';
import type { WantedItem, CreateWantedItemInput } from '@siliconvault/shared';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { ComboBox } from '../ui/ComboBox';
import { useTranslation } from '../../i18n';
import { useVault } from '../../context/VaultContext';
import { usePlatformLabel } from '../../hooks/usePlatformLabel';
import { useTaxonomySuggestions } from '../../hooks/useTaxonomy';
import styles from './WantedItemForm.module.css';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateWantedItemInput) => Promise<void>;
  editItem?: WantedItem | null;
  isLoading?: boolean;
}

export function WantedItemForm({ isOpen, onClose, onSubmit, editItem, isLoading }: Props) {
  const { t } = useTranslation();
  const { vaultTypeDef } = useVault();
  const platformLabel = usePlatformLabel();
  const defaultCategory = editItem?.category ?? (vaultTypeDef?.categories[0] ?? CATEGORIES[0] ?? '');
  const defaultPlatform = editItem?.platform ?? (vaultTypeDef?.brands[0] ?? PLATFORMS[0] ?? '');
  const [name, setName] = useState(editItem?.name ?? '');
  const [category, setCategory] = useState(defaultCategory);
  const [platform, setPlatform] = useState(defaultPlatform);
  const [subcategory, setSubcategory] = useState(editItem?.subcategory ?? '');
  const [conditionMin, setConditionMin] = useState(editItem?.conditionMin ?? '');
  const [videoSystem, setVideoSystem] = useState(editItem?.videoSystem ?? '');
  const [description, setDescription] = useState(editItem?.description ?? '');
  const [isPublic, setIsPublic] = useState(editItem?.isPublic ?? true);

  const { suggestions: categorySuggestions } = useTaxonomySuggestions('category');
  const { suggestions: platformSuggestions } = useTaxonomySuggestions('platform');
  const { suggestions: subcategorySuggestions } = useTaxonomySuggestions('subcategory', category, platform);

  // Build a vault-aware subcategory placeholder from the first few suggestions
  const subcategoryPlaceholder = subcategorySuggestions.length > 0
    ? `e.g., ${subcategorySuggestions.slice(0, 3).join(', ')}`
    : t('itemForm.subcategoryPlaceholder');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    await onSubmit({
      name,
      category,
      platform,
      conditionMin: conditionMin ? conditionMin as any : null,
      videoSystem: videoSystem ? videoSystem as any : null,
      subcategory: subcategory.trim() || null,
      description,
      isPublic,
    });
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={editItem ? t('community.editWantedItem') : t('community.addWantedItem')}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>{t('common.cancel')}</Button>
          <Button onClick={() => handleSubmit({ preventDefault: () => {} } as any)} disabled={!name.trim() || isLoading}>
            {isLoading ? t('settings.saving') : editItem ? t('community.save') : t('community.add')}
          </Button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className={styles.form}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{t('itemForm.name')}</label>
          <input className={styles.formInput} value={name} onChange={(e) => setName(e.target.value)} required maxLength={255} />
        </div>
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <ComboBox
              label={t('itemForm.category')}
              value={category}
              onChange={setCategory}
              suggestions={categorySuggestions}
              required
            />
          </div>
          <div className={styles.formGroup}>
            <ComboBox
              label={platformLabel}
              value={platform}
              onChange={setPlatform}
              suggestions={platformSuggestions}
              required
            />
          </div>
        </div>
        <div className={styles.formGroup}>
          <ComboBox
            label={t('itemForm.subcategory')}
            value={subcategory}
            onChange={setSubcategory}
            suggestions={subcategorySuggestions}
            placeholder={subcategoryPlaceholder}
          />
        </div>
        <div className={styles.row}>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('community.minCondition')}</label>
            <select className={styles.formSelect} value={conditionMin} onChange={(e) => setConditionMin(e.target.value)}>
              <option value="">{t('community.anyCondition')}</option>
              {CONDITIONS.map((c) => <option key={c} value={c}>{t(`condition.${c}` as any)}</option>)}
            </select>
          </div>
          <div className={styles.formGroup}>
            <label className={styles.formLabel}>{t('itemForm.videoSystem')}</label>
            <select className={styles.formSelect} value={videoSystem} onChange={(e) => setVideoSystem(e.target.value)}>
              <option value="">—</option>
              {VIDEO_SYSTEMS.map((vs) => <option key={vs} value={vs}>{t(`videoSystem.${vs}` as any)}</option>)}
            </select>
          </div>
        </div>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{t('itemForm.description')}</label>
          <textarea className={styles.formTextarea} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={2000} />
        </div>
        <div className={styles.toggleRow}>
          <div className={styles.toggleLabel}>
            <span className={styles.toggleTitle}>{t('community.publiclyVisible')}</span>
            <span className={styles.toggleDesc}>{t('community.publiclyVisibleDesc')}</span>
          </div>
          <label className={styles.toggle}>
            <input type="checkbox" checked={isPublic} onChange={(e) => setIsPublic(e.target.checked)} />
            <span className={styles.toggleTrack} />
          </label>
        </div>
      </form>
    </Modal>
  );
}
